import { EventInfo, isTeamEvent } from '@/api/events/types/types';
import { URLS } from '@/data';
import { SharedEntryPicks } from '@/feature/entry-share/api/types';
import { Outcome, ProjectionType } from '@/types/api.generated';
import { logger } from '@/utils/logging';
import { decode as base64Decode } from 'base-64';

export const LOG_TAG = '[PartnerLink]';

export const SHORT_PARTNER_LINK = `${URLS.APP_LINKS_URL}/referral`;
export const DEEP_LINK_REFERRAL = 'betrpickem://referral';

export const isPartnerLink = (url: string | null): boolean => {
    if (!url) {
        return false;
    }
    return url.startsWith(SHORT_PARTNER_LINK) || url.startsWith(DEEP_LINK_REFERRAL);
};

/**
 * Extracts the query string from a referral deeplink.
 * eg: https://l.betr.app/referral?picks=...&partner=... => ?picks=...&partner=...
 */
export const extractReferralQueryString = (url: string): string | undefined => {
    if (url.startsWith(SHORT_PARTNER_LINK)) {
        return url.substring(SHORT_PARTNER_LINK.length);
    } else if (url.startsWith(DEEP_LINK_REFERRAL)) {
        return url.substring(DEEP_LINK_REFERRAL.length);
    }
    return undefined;
};

export type PartnerLinkParsedPick = {
    playerId: string;
    eventId: string;
    projectionKey: string;
    outcome: Outcome;
    projectionType?: ProjectionType;
};

/**
 * Extracts the partner query param from a referral link.
 */
export const extractPartnerParam = (queryString: string): string | undefined => {
    const qs = queryString.split('?')[1];
    if (!qs) {
        return;
    }
    return new URLSearchParams(qs).get('partner') ?? undefined;
};

/**
 * Extracts the picks query param from a referral link.
 */
export const extractPicksParam = (queryString: string): string | undefined => {
    const qs = queryString.split('?')[1];
    if (!qs) {
        return;
    }
    const picks = new URLSearchParams(qs).get('picks');
    if (picks) {
        return picks;
    }
};

/**
 * Parses the picks query param into PartnerLinkParsedPick.
 * The picks param is base64 encoded. Format after decoding: playerId:eventId:projectionKey:outcome:projectionType
 */
export const parsePicksParam = (picksParams?: string): PartnerLinkParsedPick[] => {
    if (!picksParams) {
        return [];
    }
    const decoded = base64Decode(picksParams);
    return decoded.split(',').map(param => {
        const [playerId, eventId, projectionKey, outcomeStr, projectionTypeStr] = param.split(':');
        const outcome = outcomeStr?.toUpperCase() === 'LESS' ? Outcome.Less : Outcome.More;
        const projectionType = parseProjectionType(projectionTypeStr);
        return { playerId, eventId, projectionKey: projectionKey?.toUpperCase(), outcome, projectionType };
    });
};

const parseProjectionType = (projType?: string): ProjectionType | undefined => {
    if (!projType) {
        return;
    }
    const type = projType.toUpperCase();
    const match = Object.values(ProjectionType).find(t => t === type);
    return match;
};

/**
 * Maps PartnerLinkParsedPick[] directly to SharedEntryPicks for AddLineupModal.
 */
export const mapPartnerPicksToSharedEntryPicks = (
    parsedPicks: PartnerLinkParsedPick[],
    events: EventInfo[]
): SharedEntryPicks => {
    return parsedPicks.reduce<SharedEntryPicks>((acc, parsedPick) => {
        const event = events.find(ev => ev.id === parsedPick.eventId);
        if (!event) {
            logger.warn(LOG_TAG, `Event not found: ${parsedPick.eventId}`);
            return acc;
        }

        const allPlayers = isTeamEvent(event)
            ? event.teams.flatMap(team => {
                  const { players: teamPlayers, ...teamWithoutPlayers } = team;
                  return (teamPlayers ?? []).map(p => ({ ...p, team: teamWithoutPlayers }));
              })
            : (event.players ?? []).map(p => ({ ...p, team: undefined }));

        const playerMatch = allPlayers.find(p => p.id === parsedPick.playerId);
        if (!playerMatch) {
            logger.warn(LOG_TAG, `Player not found: ${parsedPick.playerId}`);
            return acc;
        }

        const { team, ...player } = playerMatch;
        const projections = player.projections?.filter(proj => proj.key === parsedPick.projectionKey) ?? [];
        // if there's no projection matching the key, fall back to regular projection, then to the first projection
        const projection =
            projections.find(proj => parsedPick.projectionType && proj.type === parsedPick.projectionType) ??
            projections.find(proj => proj.type === ProjectionType.Regular) ??
            projections[0];
        if (!projection) {
            logger.warn(LOG_TAG, `Projection not found: ${parsedPick.projectionKey} for player ${parsedPick.playerId}`);
            return acc;
        }

        acc.push({
            eventId: parsedPick.eventId,
            eventDate: event.date,
            eventStatus: event.status,
            league: event.league,
            sport: event.sport,
            outcome: parsedPick.outcome,
            teamId: team?.id,
            teamName: team?.name,
            projection,
            player: {
                id: player.id,
                firstName: player.firstName,
                lastName: player.lastName,
                icon: player.icon,
                position: player.position,
                jerseyNumber: player.jerseyNumber,
                record: player.record,
                rank: player.rank,
                attributes: player.attributes,
            },
        });

        return acc;
    }, []);
};
